import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { useCartStore } from '../store/cartStore'
import apiService from '../services/api'
import { shouldShowFeature, isEnterpriseUser, getUserType } from '../utils/userPermissions'
import { UserRole } from '../types'
import {
  User,
  LogOut,
  Menu,
  X,
  Search,
  Heart,
  ShoppingCart,
  // Bell,
  Wallet,
  HardHat,
  ArrowLeft,
  Building2,
  Shield,
  Settings,
  LayoutDashboard
} from 'lucide-react'
import NotificationDropdown from './NotificationDropdown'
import ThemeToggle from './ThemeToggle'
import LanguageToggle from './LanguageToggle'
import { useLocalization } from '../hooks/useLocalization'

export default function Navbar() {
  const { t } = useLocalization()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const { user, isAuthenticated } = useAuthStore()
  const { getTotalItems, toggleCart, setCurrentUser } = useCartStore()
  const navigate = useNavigate()
  const location = useLocation()
  const profileMenuRef = useRef<HTMLDivElement>(null)
  
  // Check if we're in Enterprise mode, Government mode, or if user is enterprise/agency
  const isEnterpriseMode = location.pathname.startsWith('/enterprise') ||
                          location.pathname.startsWith('/roi') ||
                          isEnterpriseUser(user)

  const isGovernmentMode = location.pathname.startsWith('/gov') ||
                          getUserType(user) === 'AGENCY'

  // Special case: Enterprise Buyer on HOME page should hide marketplace items
  const isEnterpriseBuyerOnHome = location.pathname === '/' &&
                                 user?.role === 'ENTERPRISE_BUYER'

  // Sync cart with current user
  useEffect(() => {
    const userId = user?.id || null
    setCurrentUser(userId)
  }, [user?.id, setCurrentUser])

  // Close profile menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false)
      }
    }

    if (isProfileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isProfileMenuOpen])

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false)
  }, [window.location.pathname])

  const handleLogout = async () => {
    setIsLoggingOut(true)
    try {
      await apiService.logout()
      navigate('/')
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setIsLoggingOut(false)
      setIsProfileMenuOpen(false)
    }
  }

  const cartItemCount = getTotalItems()

  return (
    <nav className="bg-white dark:bg-gray-900 shadow-lg md:relative fixed top-0 left-0 right-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Left side - Logo and main nav */}
          <div className="flex">
            {/* Logo - hidden on mobile only when Sign up is shown (guest), to free space */}
            <Link to="/" className={`items-center space-x-2 ${!isAuthenticated ? 'hidden md:flex' : 'flex'}`}>
              <div className="w-8 h-8 bg-gray-700 dark:bg-gray-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">E</span>
              </div>
              <span className="text-xl font-bold text-gray-900 dark:text-white">Evoltbay  </span>
            </Link>

            {/* Main navigation */}
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              {isGovernmentMode ? (
                <>
                  {/* Government Navigation */}
                  <Link
                    to="/government"
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                      location.pathname === '/government'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    <Shield className="h-4 w-4 mr-1" />
                    {t('nav.dashboard')}
                  </Link>
                  <Link
                    to="/gov/rfqs"
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                      location.pathname === '/gov/rfqs'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    {t('nav.rfqs')}
                  </Link>
                  <Link
                    to="/gov/proposals"
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                      location.pathname === '/gov/proposals'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    {t('nav.proposals')}
                  </Link>
                  <Link
                    to="/gov/vendors"
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                      location.pathname === '/gov/vendors'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    {t('nav.vendors')}
                  </Link>
                </>
              ) : !isEnterpriseMode && !isEnterpriseBuyerOnHome ? (
                <>
                  {shouldShowFeature(user, 'canAccessMarketplace') && (
                    <Link
                      to="/products"
                      className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                    >
                      {t('nav.browse')}
                    </Link>
                  )}
                  {shouldShowFeature(user, 'canAccessAuctions') && (
                    <Link
                      to="/auctions"
                      className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                    >
                      {t('nav.auctions')}
                    </Link>
                  )}
                  {shouldShowFeature(user, 'canAccessCategories') && (
                    <Link
                      to="/categories"
                      className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                    >
                      {t('nav.categories')}
                    </Link>
                  )}
                  {shouldShowFeature(user, 'canAccessEnterprise') && (
                    <Link
                      to="/enterprise"
                      className="border-transparent text-sky-600 hover:border-sky-300 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium space-x-1"
                    >
                      <HardHat className="h-4 w-4" />
                      <span>{t('nav.enterprise')}</span>
                      <span className="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold">NEW</span>
                    </Link>
                  )}
                </>
              ) : isEnterpriseBuyerOnHome ? (
                <>
                  {/* Enterprise Buyer on HOME - Show only Enterprise link */}
                  {shouldShowFeature(user, 'canAccessEnterprise') && (
                    <Link
                      to="/enterprise"
                      className="border-transparent text-sky-600 hover:border-sky-300 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium space-x-1"
                    >
                      <HardHat className="h-4 w-4" />
                      <span>{t('nav.enterprise')}</span>
                      <span className="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold">NEW</span>
                    </Link>
                  )}
                </>
              ) : (
                <>
                  {/* Enterprise Navigation */}
                  {!isEnterpriseUser(user) && (
                    <Link
                      to="/"
                      className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium space-x-1"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      <span>{t('nav.backToEvoltbay')}</span>
                    </Link>
                  )}
                  {shouldShowFeature(user, 'canAccessEnterprise') && (
                    <Link
                      to="/enterprise"
                      className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                        location.pathname === '/enterprise' 
                          ? 'border-blue-500 text-blue-600' 
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                      }`}
                    >
                      <HardHat className="h-4 w-4 mr-1" />
                      {t('nav.vendorDirectory')}
                    </Link>
                  )}
                  {shouldShowFeature(user, 'canAccessROICalculator') && (
                    <Link
                      to="/roi-calculator"
                      className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                        location.pathname === '/roi-calculator' 
                          ? 'border-blue-500 text-blue-600' 
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                      }`}
                    >
                      {t('nav.roiSimulator')}
                    </Link>
                  )}
                  {/* Enterprise Vendor Navigation */}
                  {(user?.role === 'VENDOR' && user?.isEnterprise) || user?.role === 'ENTERPRISE_VENDOR' ? (
                    <>
                      <Link
                        to="/enterprise/vendor-rfqs"
                        className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                          location.pathname === '/enterprise/vendor-rfqs'
                            ? 'border-blue-500 text-blue-600' 
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                        }`}
                      >
                        {t('nav.browseRfqs')}
                      </Link>
                      <Link
                        to="/quotes/vendor"
                        className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                          location.pathname === '/quotes/vendor'
                            ? 'border-blue-500 text-blue-600' 
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                        }`}
                      >
                        {t('nav.myResponses')}
                      </Link>
                    </>
                  ) : (user?.role === 'BUYER' && user?.isEnterprise) || user?.role === 'ENTERPRISE_BUYER' ? (
                    <>
                      <Link
                        to="/enterprise/rfqs"
                        className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                          location.pathname === '/enterprise/rfqs'
                            ? 'border-blue-500 text-blue-600' 
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                        }`}
                      >
                        {t('nav.myRfqs')}
                      </Link>
                    </>
                  ) : shouldShowFeature(user, 'canViewQuotes') && (
                    <Link
                      to="/dashboard?tab=quotes"
                      className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                        location.pathname === '/dashboard' && location.search.includes('tab=quotes')
                          ? 'border-blue-500 text-blue-600' 
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                      }`}
                    >
                      {t('nav.myQuotes')}
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Center - Search (only for non-government users, hidden for admin) */}
          {!isGovernmentMode && shouldShowFeature(user, 'canSearchProducts') && (
            <div className="hidden sm:flex sm:items-center sm:max-w-xs sm:w-full sm:mx-4">
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder={t('nav.searchProducts')}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 dark:focus:placeholder-gray-300 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
            </div>
          )}

          {/* Right side - Auth and user actions (all in one row inside navbar) */}
          <div className="flex flex-row items-center gap-2 sm:gap-4">
            {isAuthenticated ? (
              <>
                {/* Authenticated user menu - Heart (wishlist) uses permission */}
                {shouldShowFeature(user, 'showWishlistButton') && (
                  <button className="text-gray-500 hover:text-gray-700">
                    <Heart className="h-6 w-6" />
                  </button>
                )}
                <NotificationDropdown />

                {/* Language Toggle */}
                <div>
                  <LanguageToggle />
                </div>

                {/* Shopping Cart - Only for regular customers (not enterprise users) */}
                {shouldShowFeature(user, 'showShoppingCart') && !isEnterpriseUser(user) && (
                  <button
                    onClick={toggleCart}
                    className="relative text-gray-500 hover:text-gray-700 p-2"
                  >
                    <ShoppingCart className="h-6 w-6" />
                    {cartItemCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-primary-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {cartItemCount > 9 ? '9+' : cartItemCount}
                      </span>
                    )}
                  </button>
                )}

                {/* Sell Button - Only for regular vendors (not enterprise users) */}
                {shouldShowFeature(user, 'showSellButton') && !isEnterpriseUser(user) && (
                  <Link
                    to="/sell"
                    className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                  >
                    {t('nav.sell')}
                  </Link>
                )}

                {/* Profile dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                    className="flex items-center space-x-2 text-gray-500 hover:text-gray-700"
                  >
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center overflow-hidden">
                      {user?.avatar ? (
                        <img
                          src={user.avatar}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : getUserType(user) === 'AGENCY' ? (
                        <Shield className="h-5 w-5 text-primary-600" />
                      ) : (
                        <User className="h-5 w-5 text-primary-600" />
                      )}
                    </div>
                    <span className="hidden sm:block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Hi {isEnterpriseUser(user) ? (user?.companyName || user?.firstName || 'User') : (user?.firstName || 'User')}!
                    </span>
                  </button>

                  {/* Profile dropdown menu */}
                  {isProfileMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50" ref={profileMenuRef}>
                      {/* Special simplified menu for ADMIN users */}
                      {user && user.role === UserRole.ADMIN ? (
                        <>
                          <Link
                            to="/admin/settings"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 flex items-center space-x-2"
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            <Settings className="h-4 w-4" />
                            <span>{t('nav.settings')}</span>
                          </Link>
                          <Link
                            to="/admin"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 flex items-center space-x-2"
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            <LayoutDashboard className="h-4 w-4" />
                            <span>{t('nav.dashboard')}</span>
                          </Link>
                          <hr className="my-1" />
                          <button
                            onClick={handleLogout}
                            disabled={isLoggingOut}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 flex items-center space-x-2 disabled:opacity-50"
                          >
                            <LogOut className="h-4 w-4" />
                            <span>{isLoggingOut ? t('nav.signingOut') : t('nav.signOut')}</span>
                          </button>
                        </>
                      ) : (
                        <>
                          {getUserType(user) === 'AGENCY' && (
                            <Link
                              to="/gov/dashboard"
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                              onClick={() => setIsProfileMenuOpen(false)}
                            >
                              <Shield className="h-4 w-4" />
                              <span>{t('nav.agencyDashboard')}</span>
                            </Link>
                          )}
                          {(getUserType(user) === 'ENTERPRISE_BUYER' || getUserType(user) === 'ENTERPRISE_VENDOR') && (
                            <Link
                              to="/enterprise/dashboard"
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                              onClick={() => setIsProfileMenuOpen(false)}
                            >
                              <Building2 className="h-4 w-4" />
                              <span>{t('nav.enterpriseDashboard')}</span>
                            </Link>
                          )}
                          {shouldShowFeature(user, 'canViewWallet') && !isEnterpriseUser(user) && (
                            <Link
                              to="/wallet"
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                              onClick={() => setIsProfileMenuOpen(false)}
                            >
                              <Wallet className="h-4 w-4" />
                              <span>{t('nav.wallet')}</span>
                            </Link>
                          )}
                          {/* Profiles section for enterprise users */}
                          {isEnterpriseUser(user) ? (
                            <>
                              <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('nav.profiles')}</div>
                              <Link
                                to="/company/profile/manage"
                                className="block px-6 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                                onClick={() => setIsProfileMenuOpen(false)}
                              >
                                <Building2 className="h-4 w-4" />
                                <span>{t('nav.company')}</span>
                              </Link>
                              <Link
                                to="/profile"
                                className="block px-6 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                                onClick={() => setIsProfileMenuOpen(false)}
                              >
                                <User className="h-4 w-4" />
                                <span>{t('nav.personal')}</span>
                              </Link>
                            </>
                          ) : (
                            <Link
                              to="/profile"
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                              onClick={() => setIsProfileMenuOpen(false)}
                            >
                              <User className="h-4 w-4" />
                              <span>{t('nav.profile')}</span>
                            </Link>
                          )}
                          <Link
                            to="/messages"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            {t('nav.messages')}
                          </Link>
                          {user && user.role === UserRole.ADMIN && (
                            <Link
                              to="/admin"
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 font-medium text-purple-700"
                              onClick={() => setIsProfileMenuOpen(false)}
                            >
                              {t('nav.adminDashboard')}
                            </Link>
                          )}
                          {user && user.role === UserRole.ADMIN && (
                            <Link
                              to="/admin/settings"
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 font-medium text-blue-700"
                              onClick={() => setIsProfileMenuOpen(false)}
                            >
                              {t('nav.adminSettings')}
                            </Link>
                          )}
                          {/* {user?.role === 'ADMIN' && (
                            <Link
                              to="/admin/gov-agencies"
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 font-medium text-blue-700"
                              onClick={() => setIsProfileMenuOpen(false)}
                            >
                              Gov Agency Approvals
                            </Link>
                          )} */}
                          {shouldShowFeature(user, 'canViewMyOrders') && !isEnterpriseUser(user) && (
                            <Link
                              to="/orders"
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                              onClick={() => setIsProfileMenuOpen(false)}
                            >
                              {t('nav.myOrders')}
                            </Link>
                          )}
                          {shouldShowFeature(user, 'canViewMyListings') && !isEnterpriseUser(user) && (
                            <Link
                              to="/products/my"
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                              onClick={() => setIsProfileMenuOpen(false)}
                            >
                              {t('nav.myProducts')}
                            </Link>
                          )}

                          {/* Theme Toggle */}
                          <div className="px-4 py-2">
                            <ThemeToggle />
                          </div>

                          <hr className="my-1" />
                          <button
                            onClick={handleLogout}
                            disabled={isLoggingOut}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 flex items-center space-x-2 disabled:opacity-50"
                          >
                            <LogOut className="h-4 w-4" />
                            <span>{isLoggingOut ? t('nav.signingOut') : t('nav.signOut')}</span>
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Guest users can also see the cart (but not in enterprise mode) */}
                {!isEnterpriseMode && (
                  <button
                    onClick={toggleCart}
                    className="relative text-gray-500 hover:text-gray-700 p-2"
                  >
                    <ShoppingCart className="h-6 w-6" />
                    {cartItemCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-primary-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {cartItemCount > 9 ? '9+' : cartItemCount}
                      </span>
                    )}
                  </button>
                )}

                {/* Language Toggle */}
                <div>
                  <LanguageToggle />
                </div>

                {/* Guest user buttons - stacked below Language Toggle on mobile */}
                <Link
                  to="/login"
                  className="text-gray-500 hover:text-gray-700 px-3 py-2 text-sm font-medium"
                >
                  {t('nav.signIn')}
                </Link>
                <Link
                  to="/register"
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                >
                  {t('nav.signUp')}
                </Link>
              </>
            )}

            {/* Mobile menu button - Hidden when MobileFooter is active (mobile screens) */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="hidden text-gray-500 hover:text-gray-700"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="sm:hidden bg-white dark:bg-gray-900">
          <div className="pt-2 pb-3 space-y-1">
            {isGovernmentMode ? (
              <>
                {/* Government Agency Mobile Navigation */}
                <Link
                  to="/gov/dashboard"
                  className={`flex items-center px-4 py-2 text-sm ${
                    location.pathname === '/gov/dashboard'
                      ? 'text-blue-600 bg-blue-50 border-l-4 border-blue-600 font-medium dark:bg-blue-900/20'
                      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Shield className="h-4 w-4 mr-2" />
                  {t('nav.dashboard')}
                </Link>
                <Link
                  to="/gov/rfqs/create"
                  className={`block px-4 py-2 text-sm ${
                    location.pathname === '/gov/rfqs/create' 
                      ? 'text-blue-600 bg-blue-50 border-l-4 border-blue-600 font-medium' 
                      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('gov.createRfq')}
                </Link>
                <Link
                  to="/gov/settings"
                  className={`block px-4 py-2 text-sm ${
                    location.pathname === '/gov/settings' 
                      ? 'text-blue-600 bg-blue-50 border-l-4 border-blue-600 font-medium' 
                      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('nav.settings')}
                </Link>
              </>
            ) : !isEnterpriseMode ? (
              <>
                {shouldShowFeature(user, 'canAccessMarketplace') && (
                  <Link
                    to="/products"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('nav.browse')}
                  </Link>
                )}
                {shouldShowFeature(user, 'canAccessAuctions') && (
                  <Link
                    to="/auctions"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('nav.auctions')}
                  </Link>
                )}
                {shouldShowFeature(user, 'canAccessCategories') && (
                  <Link
                    to="/categories"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('nav.categories')}
                  </Link>
                )}
                {shouldShowFeature(user, 'canAccessEnterprise') && (
                  <Link
                    to="/enterprise"
                    className="flex items-center px-4 py-2 text-sm text-sky-600 hover:bg-gray-100 font-medium"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <HardHat className="h-4 w-4 mr-2" />
                    {t('nav.enterprise')}
                    <span className="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold ml-auto">NEW</span>
                  </Link>
                )}
              </>
            ) : (
              <>
                {/* Enterprise Mobile Navigation */}
                {!isEnterpriseUser(user) && (
                  <Link
                    to="/"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    {t('nav.backToEvoltbay')}
                  </Link>
                )}
                {shouldShowFeature(user, 'canAccessEnterprise') && (
                  <Link
                    to="/enterprise"
                    className={`flex items-center px-4 py-2 text-sm ${
                      location.pathname === '/enterprise' 
                        ? 'text-blue-600 bg-blue-50 border-l-4 border-blue-600 font-medium' 
                        : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <HardHat className="h-4 w-4 mr-2" />
                    {t('nav.enterprise')}
                  </Link>
                )}
                {/* Enterprise Vendor Mobile Navigation */}
                {(user?.role === 'VENDOR' && user?.isEnterprise) || user?.role === 'ENTERPRISE_VENDOR' ? (
                  <>
                    <Link
                      to="/enterprise/vendor-rfqs"
                      className={`flex items-center px-4 py-2 text-sm ${
                        location.pathname === '/enterprise/vendor-rfqs'
                          ? 'text-blue-600 bg-blue-50 border-l-4 border-blue-600 font-medium' 
                          : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t('nav.browseRfqs')}
                    </Link>
                    <Link
                      to="/quotes/vendor"
                      className={`flex items-center px-4 py-2 text-sm ${
                        location.pathname === '/quotes/vendor'
                          ? 'text-blue-600 bg-blue-50 border-l-4 border-blue-600 font-medium' 
                          : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t('nav.myResponses')}
                    </Link>
                  </>
                ) : (user?.role === 'BUYER' && user?.isEnterprise) || user?.role === 'ENTERPRISE_BUYER' ? (
                  <>
                    <Link
                      to="/enterprise/rfqs"
                      className={`flex items-center px-4 py-2 text-sm ${
                        location.pathname === '/enterprise/rfqs'
                          ? 'text-blue-600 bg-blue-50 border-l-4 border-blue-600 font-medium' 
                          : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t('nav.myRfqs')}
                    </Link>
                  </>
                ) : shouldShowFeature(user, 'canAccessROICalculator') && (
                  <Link
                    to="/roi-calculator"
                    className={`block px-4 py-2 text-sm ${
                      location.pathname === '/roi-calculator' 
                        ? 'text-blue-600 bg-blue-50 border-l-4 border-blue-600 font-medium' 
                        : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('nav.roiSimulator')}
                  </Link>
                )}
              </>
            )}
            
            {/* Mobile search - only for non-government, non-enterprise, non-admin users */}
            {!isGovernmentMode && !isEnterpriseUser(user) && shouldShowFeature(user, 'canSearchProducts') && (
              <div className="px-4 py-2">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder={t('nav.searchProducts')}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 dark:focus:placeholder-gray-300 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
              </div>
            )}

            {/* Mobile Cart - Only for regular customers */}
            {shouldShowFeature(user, 'showShoppingCart') && (
              <button
                onClick={() => {
                  toggleCart()
                  setIsMenuOpen(false)
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <ShoppingCart className="h-5 w-5 mr-3" />
                {t('nav.cart')} ({cartItemCount})
              </button>
            )}

            {isAuthenticated && (
              <>
                <hr className="my-2" />
                {user?.role === UserRole.ADMIN && (
                  <Link
                    to="/dashboard"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('nav.dashboard')}
                  </Link>
                )}
                {getUserType(user) === 'AGENCY' && (
                  <Link
                    to="/gov/dashboard"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Shield className="h-5 w-5 mr-3" />
                    {t('nav.agencyDashboard')}
                  </Link>
                )}
                {shouldShowFeature(user, 'canViewWallet') && !isEnterpriseUser(user) && (
                  <Link
                    to="/wallet"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Wallet className="h-5 w-5 mr-3" />
                    {t('nav.wallet')}
                  </Link>
                )}
                {/* Profiles section for enterprise users */}
                {isEnterpriseUser(user) ? (
                  <>
                    <div className="px-4 py-2 text-xs text-gray-500 uppercase tracking-wider">{t('nav.profiles')}</div>
                    <Link
                      to="/company/profile/manage"
                      className="flex items-center px-6 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Building2 className="h-5 w-5 mr-3" />
                      {t('nav.company')}
                    </Link>
                    <Link
                      to="/profile"
                      className="flex items-center px-6 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <User className="h-5 w-5 mr-3" />
                      {t('nav.personal')}
                    </Link>
                  </>
                ) : (
                  <Link
                    to="/profile"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <User className="h-5 w-5 mr-3" />
                    {t('nav.profile')}
                  </Link>
                )}
                {user?.role === UserRole.ADMIN && (
                  <Link
                    to="/admin"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 font-medium text-purple-700"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('nav.adminDashboard')}
                  </Link>
                )}
                {user?.role === UserRole.ADMIN && (
                  <Link
                    to="/admin/settings"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 font-medium text-blue-700"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('nav.adminSettings')}
                  </Link>
                )}
                {user?.role === 'ADMIN' && (
                  <Link
                    to="/admin/gov-agencies"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 font-medium text-blue-700"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t('nav.govAgencyApprovals')}
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                >
                  {isLoggingOut ? 'Signing out...' : 'Sign out'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  )
} 